// Thin API client for per-user saved filters ("saved views").
//
// Backend contract (per-user REST framing; user resolved from the JWT):
//   GET    /users/filters?group={groupId}      list all my saved filters for a group
//   POST   /users/filters                       create a named preset OR upsert the
//                                                last-used row (name === '' sentinel)
//   PUT    /users/filters/{id}                   rename / set-default a preset
//   DELETE /users/filters/{id}                   delete a preset
//
// These are plain async functions (no Vue lifecycle) so both Options-API and
// Composition-API pages can call them. useSavedFilters wraps them with reactive
// state; SavedViewsDrawer can call them directly.

import axios from 'axios'
import { Auth } from 'aws-amplify'
import { getCached } from '@/util/groupDataCache'
// api-config is a CommonJS module (module.exports); pull API_LOCATION off it.
import apiConfig from '@/api-config'

const API_LOCATION: string = (apiConfig as any).API_LOCATION

/** A saved filter row as returned by the backend. `name === ''` is the auto-saved last-used row. */
export interface SavedFilterRow {
  id: number
  view_key: string
  name: string
  filters: Record<string, any>
  is_default: 0 | 1
  visibility: string
  created_at?: string
  updated_at?: string
}

async function authHeader(): Promise<Record<string, string>> {
  const session = await Auth.currentSession()
  return { Authorization: session.getIdToken().getJwtToken() }
}

function currentGroupId(): number | null {
  try {
    const group = JSON.parse(localStorage.getItem('group') || '{}')
    return group?.id ?? null
  } catch (e) {
    return null
  }
}

/**
 * Parse an axios error body that may be a JSON string (new-convention lambdas)
 * or a plain string (legacy). Returns a best-effort human message.
 */
export function parseSavedFilterError(err: any): string {
  const data = err?.response?.data
  if (data && typeof data === 'object' && data.error) return data.error
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data)
      if (parsed && parsed.error) return parsed.error
    } catch (e) { /* not JSON, fall through */ }
    return data
  }
  return 'Something went wrong'
}

/** Fetch every saved filter row for the current group (all view_keys). */
export async function fetchSavedFilters(): Promise<SavedFilterRow[]> {
  const groupId = currentGroupId()
  if (groupId == null) return []
  // Cached per group and shared between every FilterBar and saved-views surface on
  // the page; the write calls below evict it through the axios interceptor.
  return getCached('saved-filters', async () => {
    const headers = await authHeader()
    const url = `${API_LOCATION}users/filters?group=${groupId}`
    const res = await axios.get(url, { headers })
    const rows: SavedFilterRow[] = res.data?.filters || []
    return rows
  })
}

/**
 * Upsert the auto-saved last-used row for a view (name === ''). Idempotent on the
 * backend via INSERT ... ON DUPLICATE KEY UPDATE. Returns the row id.
 */
export async function saveLastUsedFilters(viewKey: string, filters: Record<string, any>): Promise<number | null> {
  const groupId = currentGroupId()
  if (groupId == null) return null
  const headers = await authHeader()
  const url = `${API_LOCATION}users/filters`
  const res = await axios.post(url, { group: groupId, view_key: viewKey, name: '', filters }, { headers })
  return res.data?.id ?? null
}

/** Create a named preset for a view. Returns the new row id. */
export async function createPreset(viewKey: string, name: string, filters: Record<string, any>): Promise<number | null> {
  const groupId = currentGroupId()
  if (groupId == null) return null
  const headers = await authHeader()
  const url = `${API_LOCATION}users/filters`
  const res = await axios.post(url, { group: groupId, view_key: viewKey, name, filters }, { headers })
  return res.data?.id ?? null
}

/** Rename a preset. */
export async function renamePreset(id: number, name: string): Promise<void> {
  const headers = await authHeader()
  await axios.put(`${API_LOCATION}users/filters/${id}`, { name }, { headers })
}

/** Set or clear a preset as the user's default for its view. */
export async function setPresetDefault(id: number, isDefault: boolean): Promise<void> {
  const headers = await authHeader()
  await axios.put(`${API_LOCATION}users/filters/${id}`, { is_default: isDefault ? 1 : 0 }, { headers })
}

/** Delete a preset. */
export async function deletePreset(id: number): Promise<void> {
  const headers = await authHeader()
  await axios.delete(`${API_LOCATION}users/filters/${id}`, { headers })
}
